import { call, takeLatest, select, put, take } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { makeBackendApi, OffersSearchSuccessResponse } from 'services/BackendApi';
import qs from 'qs';
import { InitializeQueryAction, INITIALIZE_QUERY } from '../actions';
import { offersSearchFailureAction, offersSearchSuccessAction, populateQueryAction } from '../actions';
import { clearBookingBuilderAction } from 'store/modules/bookingBuilder';
import { selectedTaSelector, selectedCompanySelector } from '../../agents';
import {
  loadSelectedCompanyViaUuidRequestAction,
  loadSelectedTaViaUuidRequestAction,
  LOAD_SELECTED_COMPANY_VIA_UUID_SUCCESS,
  LOAD_SELECTED_COMPANY_VIA_UUID_FAILURE,
  GET_TRAVEL_AGENTS_BY_COMPANY_ID_SUCCESS,
  GET_TRAVEL_AGENTS_BY_COMPANY_ID_FAILURE,
  LOAD_SELECTED_TA_VIA_UUID_SUCCESS,
  LOAD_SELECTED_TA_VIA_UUID_FAILURE,
} from '../../agents/actions';
import { isInternalUser } from '../../auth/selectors';

export function* initializeFastSearchQuerySaga(action: InitializeQueryAction) {
  try {
    const queryObj = yield qs.parse(action.queryString);

    // Extract company and TA UUIDs from query params
    const companyUuid = queryObj.companyUuid;
    const taUuid = queryObj.tauuid;

    // Load company if UUID is provided
    if (companyUuid) {
      yield put(loadSelectedCompanyViaUuidRequestAction(companyUuid));

      // Wait for company to be loaded
      const companyResult = yield take([
        LOAD_SELECTED_COMPANY_VIA_UUID_SUCCESS,
        LOAD_SELECTED_COMPANY_VIA_UUID_FAILURE,
      ]);

      if (companyResult.type === LOAD_SELECTED_COMPANY_VIA_UUID_FAILURE) {
        console.error('Failed to load company:', companyUuid);
        yield put(offersSearchFailureAction({ errors: [{ title: 'Failed to load company' }] } as any));
        return;
      }

      // Wait for travel agents to be loaded (triggered automatically by company selection)
      const taLoadResult = yield take([
        GET_TRAVEL_AGENTS_BY_COMPANY_ID_SUCCESS,
        GET_TRAVEL_AGENTS_BY_COMPANY_ID_FAILURE,
      ]);

      if (taLoadResult.type === GET_TRAVEL_AGENTS_BY_COMPANY_ID_FAILURE) {
        console.error('Failed to load travel agents for company:', companyUuid);
      }

      // Load specific TA if UUID is provided
      if (taUuid) {
        yield put(loadSelectedTaViaUuidRequestAction(taUuid));

        // Wait for TA to be loaded
        const taResult = yield take([LOAD_SELECTED_TA_VIA_UUID_SUCCESS, LOAD_SELECTED_TA_VIA_UUID_FAILURE]);

        if (taResult.type === LOAD_SELECTED_TA_VIA_UUID_FAILURE) {
          console.error('Failed to load travel agent:', taUuid);
        }
      }
    }

    // Get the selected TA after potential loading
    const selectedTa = yield select(selectedTaSelector);
    const selectedCompany = yield select(selectedCompanySelector);
    const isInternal = yield select(isInternalUser);

    // Check if SR user has both company and TA selected
    if (isInternal && (!selectedCompany || !selectedTa)) {
      console.error('SR user requires both company and TA to search');
      yield put(
        offersSearchFailureAction({
          errors: [{ title: 'Please select both company and travel agent' }],
        } as any)
      );
      yield put(clearBookingBuilderAction());
      return;
    }

    const backendApi = makeBackendApi(selectedTa?.uuid);

    // Remove the company and TA UUIDs from the query before sanitizing
    const queryForSearch = { ...queryObj };
    delete queryForSearch.companyUuid;
    delete queryForSearch.tauuid;
    delete queryForSearch.membership; // Remove membership UUID - not expected by search API
    delete queryForSearch.hotelUuid;
    delete queryForSearch.currency; // OWA-10652 - display-only param, not sent to search API

    const sanitizedQuery = backendApi.sanitizQueryObject(queryForSearch);
    // @ts-ignore
    delete sanitizedQuery.tauuid;
    // @ts-ignore
    delete sanitizedQuery.taUuid;
    // @ts-ignore
    delete sanitizedQuery.companyUuid;
    // @ts-ignore
    delete sanitizedQuery.hotelUuid;

    const result: AxiosResponse<OffersSearchSuccessResponse> = yield call(backendApi.getOffersSearch, sanitizedQuery);
    yield put(offersSearchSuccessAction(result.data));
    yield put(populateQueryAction(sanitizedQuery));
    yield put(clearBookingBuilderAction());
  } catch (e) {
    yield put(offersSearchFailureAction(e));
    yield put(clearBookingBuilderAction());
  }
}

export function* watchInitializeQuery() {
  yield takeLatest(INITIALIZE_QUERY, initializeFastSearchQuerySaga);
}
